import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  Alert,
  FlexCenter,
  FlexColumn,
  LineageGraph,
} from '@elementary/common-ui/components';
import {
  getDagDepthBoundaryFromBoundaries,
  mapGqlLineageToApiLineage,
} from '@elementary/common-ui/components/lineageGraph';
import type { HopLimit } from '@elementary/common-ui/components/lineageGraph/types';
import useIncidentLineage from '../../hooks/useIncidentLineage';
import type { IncidentDetail } from '../../types';

const TabContent = styled(FlexColumn)`
  gap: 24px;
  padding: 16px 0;
  flex: 1;
  min-height: 0;
`;

const GraphWrap = styled(Box)`
  flex: 1;
  min-height: 320px;
  width: 100%;
  position: relative;
`;

const GraphLoadingOverlay = styled(FlexCenter)`
  position: absolute;
  inset: 0;
  z-index: 5;
  background: ${({ theme }) => theme.palette.common.white}aa;
  pointer-events: none;
`;

const DEFAULT_LINEAGE_HOP_LIMIT: HopLimit = { upstream: 1, downstream: 1 };

type Props = {
  incident: IncidentDetail;
};

const IncidentLineageTab = ({ incident }: Props) => {
  const [hopLimit, setHopLimit] = useState<HopLimit>(DEFAULT_LINEAGE_HOP_LIMIT);

  useEffect(() => {
    setHopLimit(DEFAULT_LINEAGE_HOP_LIMIT);
  }, [incident.id]);

  const { lineage, isLoading, isFetching, error } = useIncidentLineage(
    incident.id,
    hopLimit,
  );

  const { assets, columns } = lineage?.lineage ?? {
    assets: {
      nodes: [],
      edges: [],
      boundaries: { upstream: 0, downstream: 0 },
    },
    columns: {
      nodes: [],
      edges: [],
      exceededLimitParentAssets: [],
      boundaries: { upstream: 0, downstream: 0 },
    },
  };

  const hasLineageData =
    !!lineage &&
    (assets.nodes.length > 0 ||
      assets.edges.length > 0 ||
      columns.nodes.length > 0 ||
      columns.edges.length > 0);

  const columnNodeIds = new Set(columns.nodes.map((n) => n.id));
  const rootAssetIds =
    lineage?.rootNodeIds.filter((id) => !columnNodeIds.has(id)) ?? [];
  const rootColumnIds =
    lineage?.rootNodeIds.filter((id) => columnNodeIds.has(id)) ?? [];

  const apiLineage = useMemo(
    () => (lineage ? mapGqlLineageToApiLineage(lineage.lineage) : null),
    [lineage],
  );

  const dagDepthBoundary =
    lineage &&
    getDagDepthBoundaryFromBoundaries(assets.boundaries, columns.boundaries);

  // The default `hopLimit` is `{1, 1}` regardless of what the incident actually has
  // upstream/downstream. Cap it to the real boundary so the hops control reflects
  // what's available (e.g. shows 0 when nothing exists in that direction).
  const displayHopLimit: HopLimit = dagDepthBoundary
    ? {
        upstream: Math.min(
          hopLimit.upstream ?? 0,
          dagDepthBoundary.incomingDepth,
        ),
        downstream: Math.min(
          hopLimit.downstream ?? 0,
          dagDepthBoundary.outgoingDepth,
        ),
      }
    : hopLimit;

  const showInitialLoader = !lineage && (isLoading || isFetching);
  const showRefetchOverlay = !!lineage && isFetching;
  const hasError = error != null;

  return (
    <TabContent>
      {hasError && (
        <Alert severity="error">
          Could not load lineage for this incident.
        </Alert>
      )}

      {showInitialLoader && (
        <GraphWrap>
          <FlexCenter sx={{ height: '100%', minHeight: 200 }}>
            <CircularProgress />
          </FlexCenter>
        </GraphWrap>
      )}

      {!showInitialLoader && hasLineageData && apiLineage && dagDepthBoundary && (
        <GraphWrap>
          {showRefetchOverlay && (
            <GraphLoadingOverlay>
              <CircularProgress size={32} />
            </GraphLoadingOverlay>
          )}
          <LineageGraph
            isControlled
            overrideAssetIds={rootAssetIds}
            overrideColumnIds={rootColumnIds}
            overrideLineageData={apiLineage}
            overrideHopLimit={displayHopLimit}
            overrideDagDepthBoundary={dagDepthBoundary}
            onHopLimitChange={setHopLimit}
            showOverlayControls
            legendSelectedLabel="Monitored"
          />
        </GraphWrap>
      )}

      {!showInitialLoader && !hasError && !hasLineageData && (
        <Typography sx={{ color: 'common.gray4' }}>
          No lineage data available for this incident.
        </Typography>
      )}
    </TabContent>
  );
};

export default IncidentLineageTab;
